// components/WishlistPopover.jsx
import { useNavigate } from "react-router-dom";
import { Popover } from "@mui/material";
import { useWishlist } from "../context/WishlistContext";
import { useCart } from "../context/CartContext";
import { FLYOUT_PAPER_PROPS } from "./CartPopover";

const formatPrice = (price) => {
  const num = typeof price === "string" ? parseFloat(price) : price;
  return isNaN(num) ? "0.00" : num.toFixed(2);
};

const Icon = ({ d, className = "w-3.5 h-3.5", strokeWidth = 1.8, fill = "none" }) => (
  <svg
    viewBox="0 0 24 24"
    fill={fill}
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d={d} />
  </svg>
);

const HEART =
  "M19.5 12.6 12 20l-7.5-7.4A4.9 4.9 0 0 1 12 6.1a4.9 4.9 0 0 1 7.5 6.5Z";
const BAG = "M4 8h16l-1.3 12.2a2 2 0 0 1-2 1.8H7.3a2 2 0 0 1-2-1.8ZM9 8V6a3 3 0 0 1 6 0v2";
const CLOSE = "M18 6 6 18M6 6l12 12";
const TRASH = "M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v5M14 11v5";

export default function WishlistPopover({ anchorEl, onClose }) {
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const { wishlistItems, removeFromWishlist } = useWishlist();
  const { addToCart } = useCart();

  const handleAddToCart = (item) => {
    addToCart(item);
    removeFromWishlist(item.id);
  };

  const go = (path) => {
    onClose?.();
    navigate(path);
  };

  const count = wishlistItems.length;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={FLYOUT_PAPER_PROPS}
    >
      <div className="ob-flyout-head">
        <div>
          <span className="ob-eyebrow">Saved for later</span>
          <div className="ob-display ob-flyout-title">Wishlist</div>
        </div>
        <div className="flex items-center gap-1.5">
          {count > 0 && (
            <span className="ob-chip">
              {count} item{count === 1 ? "" : "s"}
            </span>
          )}
          <button
            type="button"
            className="ob-iconbtn"
            onClick={onClose}
            aria-label="Close wishlist"
          >
            <Icon d={CLOSE} className="w-4 h-4" />
          </button>
        </div>
      </div>

      {count === 0 ? (
        <div className="ob-empty">
          <div className="ob-empty-glyph">
            <Icon d={HEART} className="w-6 h-6" strokeWidth={1.6} />
          </div>
          <div className="ob-display text-[22px]" style={{ color: "var(--ob-ink)" }}>
            Nothing saved yet
          </div>
          <p className="text-[12.5px] mt-1.5 max-w-[260px] mx-auto leading-relaxed">
            Tap the heart on anything you like and it will wait for you here.
          </p>
          <button
            type="button"
            onClick={() => go("/products")}
            className="ob-btn ob-btn-solid mt-5"
          >
            Browse products
          </button>
        </div>
      ) : (
        <>
          <div className="ob-flyout-body ob-scroll">
            {wishlistItems.map((item) => {
              const title = item.name || item.title || "";
              return (
                <div key={item.id} className="ob-mini">
                  <img
                    src={item.image}
                    alt={title}
                    className="ob-mini-img"
                    loading="lazy"
                    decoding="async"
                    onClick={() => item.id && go(`/item/${item.id}`)}
                  />

                  <div className="flex-1 min-w-0">
                    <div
                      className="ob-mini-title"
                      title={title}
                      onClick={() => item.id && go(`/item/${item.id}`)}
                    >
                      {title}
                    </div>
                    <div className="ob-mini-price mt-1">
                      {item.currency || "USD"} {formatPrice(item.price)}
                    </div>

                    <div className="mt-2 flex items-center justify-between gap-2">
                      <button
                        type="button"
                        onClick={() => handleAddToCart(item)}
                        className="ob-btn ob-btn-ghost ob-btn-sm"
                      >
                        <Icon d={BAG} className="w-[13px] h-[13px]" />
                        Move to bag
                      </button>
                      <button
                        type="button"
                        className="ob-miniact ob-miniact-danger"
                        onClick={() => removeFromWishlist(item.id)}
                        title="Remove"
                        aria-label={`Remove ${title} from wishlist`}
                      >
                        <Icon d={TRASH} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="ob-flyout-foot">
            <button
              type="button"
              onClick={() => go("/wishlist")}
              className="ob-btn ob-btn-solid ob-btn-block"
            >
              View wishlist
              <Icon d="m9 18 6-6-6-6" className="w-[14px] h-[14px]" strokeWidth={1.9} />
            </button>
          </div>
        </>
      )}
    </Popover>
  );
}
